import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
    LayoutDashboard, 
    Users, 
    Menu, 
    X, 
    LogOut,
    ChevronRight,
    UserPlus,
    CalendarDays,
    Mail,
    Trash2,
    ClipboardList,
    FileText,
    UserCheck,
    Truck,
    FileWarning,
    BadgeEuro,
    ArrowUpFromLine,
    Warehouse,
    Map,
    Receipt,
    Download,
    HardHat
} from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import NotificationBell from './NotificationBell';
import OfflineBanner from './OfflineBanner';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from './ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const FALLBACK_LOGO = "https://customer-assets.emergentagent.com/job_7977eb18-efae-4087-a922-da3158ec99c5/artifacts/5np07nb3_1763931680316.jpg";

const navigation = [
    { name: 'Tableau de Bord', href: '/dashboard', icon: LayoutDashboard, permKey: 'tableau_de_bord' },
    { name: 'Prospects', href: '/prospects', icon: UserPlus, permKey: 'prospects' },
    { name: 'Agenda', href: '/agenda', icon: CalendarDays, permKey: 'agenda' },
    { name: 'Inventaire', href: '/inventaire', icon: ClipboardList, permKey: 'inventaire' },
    { name: 'Devis', href: '/devis', icon: FileText, permKey: 'devis' },
    { name: 'Clients', href: '/clients', icon: UserCheck, permKey: 'clients' },
    { name: 'Facturation', href: '/facturation', icon: Receipt, permKey: 'facturation' },
    { name: 'Agenda demenagement', href: '/agenda-demenagement', icon: Truck, permKey: 'agenda_demenagement' },
    { name: 'Espace demenageurs', href: '/espace-demenageurs', icon: HardHat, permKey: 'espace_demenageurs' },
    { name: 'AOT', href: '/aot', icon: FileWarning, permKey: 'aot' },
    { name: 'Tarif AOT', href: '/tarif-aot', icon: BadgeEuro, permKey: 'tarif_aot' },
    { name: 'Monte-meuble', href: '/monte-meuble', icon: ArrowUpFromLine, permKey: 'monte_meuble' },
    { name: 'Garde meuble', href: '/garde-meuble', icon: Warehouse, permKey: 'garde_meuble' },
    { name: 'Carte groupage', href: '/carte-groupage', icon: Map, permKey: 'carte_groupage' },
    // Messagerie & Corbeille moved to Administration page (admin-only access)
    // { name: 'Messagerie', href: '/messagerie', icon: Mail, permKey: 'messagerie' },
    // { name: 'Corbeille', href: '/corbeille', icon: Trash2, permKey: 'corbeille' },
    { name: 'Administration', href: '/administration', icon: Users, adminOnly: true, permKey: 'administration' },
    // Masque temporairement
    // { name: 'Import appels OVH', href: '/import-ovh', icon: Download, adminOnly: true },
];

// Helper to determine if a user can READ a given module key.
// Rules:
//   - admin always has access
//   - if no permissions object exists at all on the user, allow (legacy)
//   - if permissions[key] does not exist, allow (module added later, default visible)
//   - otherwise read from permissions[key].lecture
export function canReadModule(user, permKey) {
    if (!user) return false;
    if (user.role === 'admin') return true;
    if (!permKey) return true;
    const perms = user.permissions;
    if (!perms || typeof perms !== 'object' || Object.keys(perms).length === 0) return true;
    const entry = perms[permKey];
    if (!entry) return true;
    return entry.lecture !== false;
}

function Sidebar({ mobile = false, onClose, logoUrl }) {
    const location = useLocation();
    const { user } = useAuth();

    const filteredNavigation = navigation.filter(item => {
        if (item.adminOnly && user?.role !== 'admin') return false;
        return canReadModule(user, item.permKey);
    });

    return (
        <div className="flex flex-col h-full bg-[#1B365D] text-white">
            {/* Logo */}
            <div className="flex items-center justify-center h-20 border-b border-white/10 bg-white/5">
                <img
                    src={logoUrl || FALLBACK_LOGO}
                    alt="Logo"
                    className="h-14 w-auto rounded-lg object-contain"
                    data-testid="sidebar-logo"
                    onError={(e) => { e.target.src = FALLBACK_LOGO; }}
                />
            </div>

            {/* Navigation — auto-fit: items distribute themselves over the
                available vertical space so the user never gets a scrollbar.
                Typography also adapts to keep things readable. */}
            {(() => {
                const n = filteredNavigation.length;
                const sizing = n <= 8
                    ? { icon: 'w-5 h-5', text: 'text-base' }
                    : n <= 14
                        ? { icon: 'w-[18px] h-[18px]', text: 'text-[15px]' }
                        : { icon: 'w-4 h-4', text: 'text-sm' };
                return (
                    <nav className="flex-1 min-h-0 px-3 py-3 flex flex-col gap-0.5">
                        {filteredNavigation.map((item) => {
                            const isActive = location.pathname === item.href;
                            const Icon = item.icon;
                            return (
                                <Link
                                    key={item.name}
                                    to={item.href}
                                    onClick={mobile ? onClose : undefined}
                                    data-testid={`nav-${item.href.replace('/', '')}`}
                                    className={`
                                        flex-1 min-h-[34px]
                                        flex items-center gap-2.5 px-3 rounded-lg transition-all duration-200
                                        ${isActive
                                            ? 'bg-[#C9A962]/20 border-l-4 border-[#C9A962] pl-2'
                                            : 'hover:bg-white/10 border-l-4 border-transparent'
                                        }
                                    `}
                                >
                                    <Icon className={`${sizing.icon} shrink-0 ${isActive ? 'text-[#C9A962]' : 'text-white/70'}`} />
                                    <span className={`${sizing.text} font-medium leading-tight ${isActive ? 'text-white' : 'text-white/80'}`}>
                                        {item.name}
                                    </span>
                                    {isActive && <ChevronRight className="w-4 h-4 ml-auto text-[#C9A962]" />}
                                </Link>
                            );
                        })}
                    </nav>
                );
            })()}

            {/* User info at bottom */}
            <div className="p-4 border-t border-white/10">
                <div className="flex items-center gap-3">
                    <Avatar className="h-10 w-10 bg-[#C9A962]">
                        <AvatarFallback className="bg-[#C9A962] text-[#1B365D] font-semibold">
                            {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                        </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                        <p className="text-xs text-white/60 capitalize">{user?.role}</p>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Header({ logoUrl, onOpenProfile }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    return (
        <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-gray-200">
            <div className="flex items-center justify-between h-16 px-4 lg:px-8">
                {/* Mobile menu button */}
                <div className="lg:hidden">
                    <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                        <SheetTrigger asChild>
                            <Button 
                                variant="ghost" 
                                size="icon"
                                data-testid="mobile-menu-button"
                            >
                                <Menu className="h-6 w-6 text-[#1B365D]" />
                            </Button>
                        </SheetTrigger>
                        <SheetContent side="left" className="p-0 w-64 bg-[#1B365D]">
                            <Sidebar mobile onClose={() => setMobileMenuOpen(false)} logoUrl={logoUrl} />
                        </SheetContent>
                    </Sheet>
                </div>

                {/* Logo for mobile */}
                <div className="lg:hidden flex items-center">
                    <img
                        src={logoUrl || FALLBACK_LOGO}
                        alt="Logo"
                        className="h-10 w-auto rounded object-contain"
                        onError={(e) => { e.target.src = FALLBACK_LOGO; }}
                    />
                </div>

                {/* Page title - hidden on mobile */}
                <div className="hidden lg:block">
                    <h1 className="text-xl font-semibold text-[#1B365D]" data-testid="app-title">
                        Gestion Luxy Move
                    </h1>
                </div>

                {/* User dropdown */}
                <div className="flex items-center gap-1">
                    <NotificationBell />
                    <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button 
                            variant="ghost" 
                            className="flex items-center gap-2"
                            data-testid="user-menu-button"
                        >
                            <Avatar className="h-8 w-8 bg-[#1B365D]">
                                <AvatarFallback className="bg-[#1B365D] text-white text-sm">
                                    {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                                </AvatarFallback>
                            </Avatar>
                            <span className="hidden sm:inline text-sm font-medium text-gray-700">
                                {user?.name}
                            </span>
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                        <div className="px-2 py-1.5">
                            <p className="text-sm font-medium">{user?.name}</p>
                            <p className="text-xs text-gray-500">{user?.email}</p>
                            <p className="text-xs text-[#C9A962] capitalize mt-1">{user?.role}</p>
                        </div>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                            onClick={handleLogout}
                            className="text-red-600 cursor-pointer"
                            data-testid="logout-button"
                        >
                            <LogOut className="w-4 h-4 mr-2" />
                            Déconnexion
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
                </div>
            </div>
        </header>
    );
}

function ProfileDialog({ open, onOpenChange }) {
    const { user, logout } = useAuth();
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open && user) {
            setEmail(user.email || '');
            setName(user.name || '');
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        }
    }, [open, user]);

    const handleSave = async () => {
        if (!currentPassword) {
            toast.error('Mot de passe actuel requis pour valider les changements');
            return;
        }
        if (newPassword && newPassword.length < 6) {
            toast.error('Nouveau mot de passe : 6 caractères minimum');
            return;
        }
        if (newPassword && newPassword !== confirmPassword) {
            toast.error('Les nouveaux mots de passe ne correspondent pas');
            return;
        }
        try {
            setSaving(true);
            const payload = {
                current_password: currentPassword,
                email: email.trim() || undefined,
                name: name.trim() || undefined,
                new_password: newPassword || undefined,
            };
            const emailChanged = (email.trim() || '').toLowerCase() !== (user.email || '').toLowerCase();
            const passwordChanged = !!newPassword;
            await axios.put(`${API_URL}/api/auth/me/profile`, payload, { withCredentials: true });
            toast.success('Compte mis à jour');
            onOpenChange(false);
            if (emailChanged || passwordChanged) {
                toast.message('Vous allez être déconnecté pour appliquer les changements.');
                setTimeout(async () => { await logout(); window.location.href = '/login'; }, 1200);
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur lors de la mise à jour');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!saving) onOpenChange(v); }}>
            <DialogContent className="max-w-md" data-testid="profile-dialog">
                <DialogTitle className="text-[#1B365D]">Mon compte</DialogTitle>
                <div className="space-y-3 mt-2">
                    <div>
                        <label className="text-xs font-medium text-gray-600">Nom</label>
                        <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name" />
                    </div>
                    <div>
                        <label className="text-xs font-medium text-gray-600">Email</label>
                        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="profile-email" />
                    </div>
                    <div className="border-t pt-3 mt-3">
                        <p className="text-xs font-semibold text-[#1B365D] mb-2">Changer le mot de passe (optionnel)</p>
                        <div className="space-y-2">
                            <div>
                                <label className="text-xs font-medium text-gray-600">Nouveau mot de passe</label>
                                <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Laisser vide pour ne pas changer" data-testid="profile-new-password" />
                            </div>
                            <div>
                                <label className="text-xs font-medium text-gray-600">Confirmer le mot de passe</label>
                                <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} data-testid="profile-confirm-password" />
                            </div>
                        </div>
                    </div>
                    <div className="border-t pt-3 mt-3">
                        <label className="text-xs font-medium text-gray-600">
                            Mot de passe actuel <span className="text-red-500">*</span>
                        </label>
                        <Input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} placeholder="Requis pour valider les modifications" data-testid="profile-current-password" />
                    </div>
                </div>
                <div className="flex gap-2 justify-end mt-4">
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Annuler</Button>
                    <Button className="bg-[#1B365D] hover:bg-[#15294a] text-white" onClick={handleSave} disabled={saving} data-testid="profile-save">
                        {saving ? 'Enregistrement…' : 'Enregistrer'}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default function Layout({ children }) {
    const { user } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [logoUrl, setLogoUrl] = useState(null);
    const [profileOpen, setProfileOpen] = useState(false);

    // Fetch logo from settings
    useEffect(() => {
        if (!user) return;
        let cancelled = false;
        axios.get(`${API_URL}/api/settings`, { withCredentials: true })
            .then((res) => {
                if (cancelled) return;
                const url = res?.data?.company?.logo_url || '';
                if (url) {
                    setLogoUrl(url.startsWith('http') ? url : `${API_URL}${url}`);
                } else {
                    setLogoUrl(null);
                }
            })
            .catch(() => { if (!cancelled) setLogoUrl(null); });
        return () => { cancelled = true; };
    }, [user]);

    // Route-level enforcement: if the current URL is the LIST page of a
    // module the user cannot read, kick back.
    useEffect(() => {
        if (!user) return;
        const path = location.pathname;
        const exact = navigation.find(n => path === n.href);
        if (!exact || !exact.permKey) return;
        const allowedByPerms = (user.role === 'admin') || canReadModule(user, exact.permKey);
        if (allowedByPerms) return;
        const fallback = navigation.find(n => {
            if (n.adminOnly && user?.role !== 'admin') return false;
            return (user.role === 'admin') || canReadModule(user, n.permKey);
        });
        navigate(fallback ? fallback.href : '/dashboard', { replace: true });
    }, [user, location.pathname, navigate]);

    return (
        <div className="min-h-screen bg-[#F3F4F6]">
            {/* Desktop Sidebar */}
            <aside className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
                <Sidebar logoUrl={logoUrl} />
            </aside>

            {/* Main content */}
            <div className="lg:pl-64 flex flex-col h-screen">
                <Header logoUrl={logoUrl} onOpenProfile={() => setProfileOpen(true)} />
                <OfflineBanner />
                <main className="flex-1 p-4 lg:p-8 overflow-y-auto">
                    {children}
                </main>
            </div>

            <ProfileDialog open={profileOpen} onOpenChange={setProfileOpen} />
        </div>
    );
}
